import { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { FadeLoader } from 'react-spinners';

// material
import {
  Box,
  Grid,
  Button,
  Card,
  Container,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  TablePagination,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';

// react-i18n
import { useTranslation } from 'react-i18next';

import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import ResourceCard from '../../sections/@dashboard/resources/ResourceCard';
import { ConfirmationDialog } from '../../components/general/ConfirmationDialog';
import { useApi } from '../../hooks/useApi';
import useQuery from '../../hooks/useQuery';

// ----------------------------------------------------------------------

const BRAND = '#032C65';
const BRAND_LIGHT = '#f0f4ff';

export default function ResourcesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [resourcesList, setResourcesList] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, per_page: 12, current_page: 1, last_page: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedHashid, setSelectedHashid] = useState(null);

  const fetchData = useQuery();
  const { doDelete } = useApi();

  const refreshList = async () => {
    setIsLoading(true);
    await fetchData('resources', search, 'desc', setResourcesList, 'created_at', setPagination, page, () => {});
    setIsLoading(false);
  };

  useEffect(() => {
    const timer = setTimeout(refreshList, 500);
    return () => clearTimeout(timer);
  }, [search, page]);

  const handleDelete = () => {
    doDelete(`resources/${selectedHashid}`, t('delete_success'), t('delete_error'), refreshList);
    setShowDeleteDialog(false);
  };

  return (
    <Page title={`Dashboard: ${t('Resources')}`}>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={4}>
          <Typography variant="h4" fontWeight={800} color={BRAND}>
            {t('Resources')}
          </Typography>
          <Button
            variant="contained"
            component={RouterLink}
            to="/dashboard/resources/new"
            startIcon={<Iconify icon="eva:plus-fill" />}
            sx={{ background: BRAND, '&:hover': { background: '#054a9e' }, borderRadius: 2, px: 3 }}
          >
            {t('New_resource')}
          </Button>
        </Stack>

        <Card>
          <Box p={3} pb={0}>
            <TextField
              size="small"
              placeholder={t('Search_events')}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              sx={{ width: { xs: '100%', sm: 340 }, background: '#fff', borderRadius: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box p={3}>
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <FadeLoader color={BRAND} loading />
              </Box>
            )}

            {!isLoading && resourcesList.length === 0 && (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 10,
                  borderRadius: 3,
                  border: '1px dashed',
                  borderColor: 'divider',
                }}
              >
                <ImageIcon sx={{ fontSize: 64, color: BRAND, opacity: 0.15, mb: 2 }} />
                <Typography color="text.secondary">{t('no_resources') || 'No resources found'}</Typography>
              </Box>
            )}

            {!isLoading && resourcesList.length > 0 && (
              <Grid container spacing={3}>
                {resourcesList.map((resource) => (
                  <ResourceCard
                    key={resource.hashid}
                    resource={resource}
                    onDelete={(hid) => { setSelectedHashid(hid); setShowDeleteDialog(true); }}
                  />
                ))}
              </Grid>
            )}
          </Box>

          {pagination.total > 0 && (
            <TablePagination
              component="div"
              rowsPerPageOptions={[]}
              count={pagination.total}
              rowsPerPage={pagination.per_page || 12}
              page={page - 1}
              onPageChange={(e, newPage) => setPage(newPage + 1)}
            />
          )}

          <ConfirmationDialog
            open={showDeleteDialog}
            onClose={() => setShowDeleteDialog(false)}
            onConfirm={handleDelete}
            message={t('delete_confirm')}
          />
        </Card>
      </Container>
    </Page>
  );
}

